import { useForm, useFieldArray } from "react-hook-form";
import {
  registerAthleteWithEventId,
  PostCandidateAthlete,
} from "../../../services/AthleteService";
import { useAxiosInstance } from "../../../services/Interceptor";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Nav from "../../../components/Nav";
import { useParams, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { GetEventsByEventId } from "../../../services/EventsService";
import {
  CheckValidCashToken,
  PostPayment,
} from "../../../services/PaymentService";
import {
  GetEventCombinePrice,
  getCombineBundleForEvent,
} from "../../../services/EventsService";
import { useDispatch } from "react-redux";
import {
  registerAccountStore,
  clearRegisterData,
} from "../../../store/slices/accountSlice";
import { useNavigate } from "react-router-dom";
import { positions } from "../../../utils/PositionStateData";
import { BackButton } from "../../../components/backButton";
import { CheckIsEmailExist } from "../../../services/AccountService";
import { useTrackEvent } from "../../../hooks/useTrackEvent";
import SpotRegisterAthleteStep1 from "./SpotRegisterAthleteStep1";
import SpotRegisterAthleteStep2 from "./SpotRegisterAthleteStep2";

const RegisterAthlete = () => {
  const { eventId } = useParams();
  const location = useLocation();
  const apiInstance = useAxiosInstance();
  const [eventNameSelected, setEventNameSelected] = useState();
  const isRegistered = false;
  const [cashTokenNew, setCashTokenNew] = useState("");
  const [combinePrice, setCombinePrice] = useState(0);
  const [step, setStep] = useState(1);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const primaryPositions = positions.filter((p) => p !== "None");
  const trackEvent = useTrackEvent();

  useEffect(() => {
    trackEvent("Registration0_EventsPage", {
      event_id: eventId,
      portal_type: "highschool",
    });
  }, [eventId, trackEvent]);

  useEffect(() => {
    const loadEvents = async () => {
      try {
        const data = await GetEventsByEventId(apiInstance, eventId);
        if (data) {
          setEventNameSelected(`${data.date} - ${data.name} - ${data.city}`);
          trackEvent("Registration1_OpenForm", {
            event_id: eventId,
            event_name: data.name,
            event_date: data.date,
            portal_type: "highschool",
          });
        }
      } catch (err) {
        console.error(err);
      }
    };
    loadEvents();
  }, [apiInstance, eventId, trackEvent]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const cashToken = searchParams.get("cashToken");

    if (cashToken) {
      setCashTokenNew(cashToken);
      trackEvent("Registration3_5_AppliedCoupon", {
        event_id: eventId,
        cash_token: cashToken,
        portal_type: "highschool",
      });
    }
  }, [location.search, eventId, trackEvent]);

  useEffect(() => {
    const fetchPricingOptions = async () => {
      try {
        const response = await GetEventCombinePrice(apiInstance, eventId);
        if (response.data) {
          setCombinePrice(response?.data?.combinePrice);
          trackEvent("Registration3_ViewedPricing", {
            event_id: eventId,
            combine_price: response?.data?.combinePrice,
            portal_type: "highschool",
          });
        }
      } catch (error) {}
    };

    fetchPricingOptions();
  }, []); // eslint-disable-line

  const validateCashToken = async (token) => {
    try {
      const response = await CheckValidCashToken(apiInstance, token);
      if (response.success === true) {
        return true;
      } else {
        toast.error("Invalid cash token.", { position: "top-center" });
        return false;
      }
    } catch (error) {
      toast.error("Failed to validate cash token.", { position: "top-center" });
    }
  };

  const checkIsEmailExist = async (email) => {
    try {
      const response = await CheckIsEmailExist(apiInstance, email);
      if (response?.data.data === false) {
        toast.warning("Email ID is already registered!", {
          position: "top-center",
        });
        return true;
      }
      return false;
    } catch (error) {
      toast.error("Failed to validate email address.", {
        position: "top-center",
      });
      return true;
    }
  };

  const stripPosition = (position) => {
    if (!position) return "";
    return position.includes("(") ? position.split("(")[0].trim() : position;
  };

  const {
    register,
    handleSubmit,
    reset,
    watch,
    control,
    setValue,
    getValues,
    clearErrors,
    trigger,
    formState: { errors },
  } = useForm({
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      confirmEmail: "",
      password: "",
      confirmPassword: "",
      age: "",
      gpa: "",
      highSchool: "",
      expectedYearOfGraduation: "",
      primaryPosition: "",
      secondaryPosition: "",
      sportsParticipation: "",
      city: "",
      state: "",
      universityOfInteres: "", // matches backend field name (trailing 't' absent in API contract)
      academicDegreeOfInteres: "", // matches backend field name (trailing 't' absent in API contract)
      role: "spotcombine",
      athletePhoneNumber: "",
      parentPhoneNumber: "",
      varsityYears: "",
      offersReceived: "no",
      athleteOffers: [],
      shouldUnregister: true,
      tshirtSize: "",
      offerAcceptanceStatus: "",
      offerAcceptanceSchool: "",
    },
  });

  const selectedPosition = watch("primaryPosition");
  const offerAcceptanceStatus = watch("offerAcceptanceStatus");
  const isCommitted = offerAcceptanceStatus !== "";

  useEffect(() => {
    if (!isCommitted) {
      setValue("offerAcceptanceSchool", "");
      clearErrors("offerAcceptanceSchool");
    }
  }, [isCommitted, setValue, clearErrors]);

  const {
    fields: offerFields,
    append,
    remove,
    replace,
  } = useFieldArray({
    control,
    name: "athleteOffers",
  });
  const offersReceived = watch("offersReceived");

  useEffect(() => {
    if (offersReceived === "yes" && offerFields.length === 0) {
      append({ schoolName: "", division: "", offerYear: "" });
    }
    if (offersReceived === "no" && offerFields.length > 0) {
      // wipe all rows in a single operation
      replace([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offersReceived]);

  const handleNextStep = async () => {
    const step1Fields = [
      "firstName",
      "lastName",
      "email",
      "confirmEmail",
      "password",
      "confirmPassword",
      "athletePhoneNumber",
      "parentPhoneNumber",
      "primaryPosition",
      "expectedYearOfGraduation",
    ];

    const isValid = await trigger(step1Fields);
    if (!isValid) return;

    const email = getValues("email");
    const isEmailExist = await checkIsEmailExist(email);
    if (isEmailExist) return;

    trackEvent("Registration2_Step1Submitted", {
      event_id: eventId,
      portal_type: "highschool",
    });

    setLoading(true);

    const rawPrimaryPosition = getValues("primaryPosition");
    try {
      // Endpoint upserts by email/eventId, so this early Step 1 capture and the
      // full Step 2 save (onSubmitStep2) never create duplicate records.
      await PostCandidateAthlete(apiInstance, {
        firstName: getValues("firstName"),
        lastName: getValues("lastName"),
        email,
        athletePhoneNumber: getValues("athletePhoneNumber"),
        parentPhoneNumber: getValues("parentPhoneNumber"),
        primaryPosition: stripPosition(
          rawPrimaryPosition === "None" ? "" : rawPrimaryPosition,
        ),
        expectedYearOfGraduation: getValues("expectedYearOfGraduation"),
        eventId: eventId,
      });
    } catch (err) {
      console.error("Failed to save candidate athlete:", err);
    }

    setLoading(false);
    setStep(2);
  };

  const onSubmitStep2 = async (data) => {
    trackEvent("Registration3_Step2Submitted", {
      event_id: eventId,
      portal_type: "highschool",
    });

    const sanitizedData = {
      ...data,
      primaryPosition: stripPosition(
        data.primaryPosition === "None" ? "" : data.primaryPosition,
      ),
      secondaryPosition: stripPosition(
        data.secondaryPosition === "None" ? "" : data.secondaryPosition,
      ),
      sportsParticipation: Array.isArray(data.sportsParticipation)
        ? data.sportsParticipation.join(", ")
        : "",
    };

    setLoading(true);

    try {
      const { password, confirmPassword, confirmEmail, ...candidatePayload } =
        sanitizedData;
      // Same upsert endpoint as the Step 1 call above; this updates the record
      // created there instead of creating a second one.
      await PostCandidateAthlete(apiInstance, {
        ...candidatePayload,
        eventId: eventId,
      });
    } catch (err) {
      console.error("Failed to save candidate athlete:", err);
    }

    try {
      if (cashTokenNew) {
        const isValid = await validateCashToken(cashTokenNew);
        if (!isValid) {
          toast.error("Invalid or expired cash token.", {
            position: "top-right",
          });
          return;
        }

        const registrationResponse = await registerAthleteWithEventId(
          apiInstance,
          sanitizedData,
          eventId,
        );

        if (registrationResponse.status === 200) {
          const userId = registrationResponse.data.id;
          const paymentPayload = {
            userId: userId,
            amount: combinePrice,
            isCombine: true,
            isPositionDrill: false,
            isAnnualSubscription: false,
            isMonthlySubscription: false,
            isCash: true,
            eventId: eventId,
            transactionId: "",
            intellipayToken: "",
          };

          await PostPayment(apiInstance, paymentPayload);
          trackEvent("Registration5_RegistrationCompleted", {
            event_id: eventId,
            payment_method: "cash",
            amount: combinePrice,
            portal_type: "highschool",
          });
          setLoading(false);
          navigate("/register/registrationsuccess");
        } else {
          trackEvent("Registration4_5_PaymentFailed", {
            event_id: eventId,
            reason: registrationResponse?.error ?? "unknown",
            portal_type: "highschool",
          });
          toast.error("Registration failed: " + registrationResponse.error, {
            position: "top-right",
          });
          return;
        }
      } else {
        const updatedData = { ...sanitizedData, eventId: eventId };
        dispatch(clearRegisterData());
        dispatch(registerAccountStore(updatedData));
        localStorage.clear();
        localStorage.setItem("registeredAccount", JSON.stringify(updatedData));

        // The route answers 204 (empty body) when the combine is in no active
        // bundle; that and any failure keep the flow unchanged.
        const bundleResponse = await getCombineBundleForEvent(
          apiInstance,
          eventId,
        );
        const bundle =
          bundleResponse.success &&
          Array.isArray(bundleResponse.data?.combines) &&
          bundleResponse.data.combines.length > 0
            ? bundleResponse.data
            : null;

        setLoading(false);

        if (bundle) {
          navigate("/athlete-combine-bundle", {
            state: { fromSpotRegister: true, eventId, bundle },
          });
        } else {
          trackEvent("Highschool_OpenedMembershipPlans", {
            event_id: eventId,
            portal_type: "highschool",
          });
          navigate("/athlete-membership", {
            state: { fromSpotRegister: true },
          });
        }
        reset();
        toast.success("Please complete the Payment.", {
          position: "top-right",
        });
      }
    } catch (error) {
      toast.error("Error Registering combine Athlete: " + error.error, {
        position: "top-right",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Nav />

      {!isRegistered && (
        <div className="w-full max-w-5xl p-6 mx-auto">
          <div className="text-white text-sm font-semibold uppercase tracking-wide mb-3">
            Step {step} of 2
          </div>

          {step === 1 && (
            <>
              {!cashTokenNew && <BackButton />}

              {/* Info area */}

              <div className="alert flex flex-row items-center bg-blue-100 p-3 rounded border-b-2 border-blue-300 mt-3">
                <div className="alert-icon flex items-center bg-blue-100 border-2 border-blue-500 justify-center h-8 w-8 flex-shrink-0 rounded-full">
                  <span className="text-blue-500">
                    <svg
                      fill="currentColor"
                      viewBox="0 0 20 20"
                      className="h-6 w-6"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                        clipRule="evenodd"
                      ></path>
                    </svg>
                  </span>
                </div>
                <div className="alert-content ml-4 w-full">
                  <div className="alert-title font-semibold text-lg text-blue-700">
                    Already registered for a previous combine?
                  </div>
                  <div className="alert-description text-sm text-blue-700">
                    Please
                    <a
                      className="text-blue-700 font-semibold hover:underline px-1"
                      href="/sign-in"
                    >
                      Sign In
                    </a>
                    to your account, select this event from your dashboard and
                    click Register.
                  </div>
                  <div className="alert-title font-semibold text-base text-blue-700 mt-3 pt-1 border-t border-blue-300 ">
                    Event details will be sent after registration.
                  </div>
                </div>
              </div>

              <div className="text-white text-left text-xl my-5">
                Selected Combine: {eventNameSelected}
              </div>
            </>
          )}

          <form
            className="container mx-auto "
            onSubmit={handleSubmit(onSubmitStep2)}
          >
            {step === 1 ? (
              <SpotRegisterAthleteStep1
                register={register}
                errors={errors}
                watch={watch}
                primaryPositions={primaryPositions}
                selectedPosition={selectedPosition}
                loading={loading}
                onNext={handleNextStep}
              />
            ) : (
              <SpotRegisterAthleteStep2
                register={register}
                errors={errors}
                watch={watch}
                control={control}
                setValue={setValue}
                getValues={getValues}
                clearErrors={clearErrors}
                apiInstance={apiInstance}
                offerFields={offerFields}
                append={append}
                remove={remove}
                replace={replace}
                offersReceived={offersReceived}
                isCommitted={isCommitted}
                loading={loading}
                onBack={() => setStep(1)}
              />
            )}
          </form>
        </div>
      )}
      <ToastContainer />
    </>
  );
};

export default RegisterAthlete;
